import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { getAssetClasses, getCountries, postComparator } from "../lib/api";
import { MultiLineChart, COLORS } from "../components/Charts";
import LocationSelector from "../components/LocationSelector";
import { Plus, X, Share2, FileDown } from "lucide-react";
import { getStoredUser } from "../components/RegisterModal";
import { API } from "../lib/api";

const DEFAULTS_IDS = ["stocks_us", "crypto_btc", "real_estate"];

// Encode/decode comparator state in URL hash
function encodeState(years, rows) {
  try {
    const payload = { y: years, a: rows.map((r) => ({ i: r.asset_id, u: r.amount_usd, c: r.city || null })) };
    return btoa(encodeURIComponent(JSON.stringify(payload)));
  } catch {
    return "";
  }
}
function decodeState(str) {
  try {
    const p = JSON.parse(decodeURIComponent(atob(str)));
    return { years: p.y, rows: p.a.map((x) => ({ asset_id: x.i, amount_usd: x.u, city: x.c || "" })) };
  } catch {
    return null;
  }
}

export default function Comparator() {
  const { t, lang } = useI18n();
  const [assets, setAssets] = useState([]);
  const [years, setYears] = useState(10);
  const [rows, setRows] = useState([]); // {asset_id, amount_usd, city}
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getAssetClasses().then((d) => setAssets(d.asset_classes || []));
    getCountries().catch(() => {});
  }, []);

  useEffect(() => {
    if (assets.length && rows.length === 0) {
      setRows(DEFAULTS_IDS.map((id) => ({ asset_id: id, amount_usd: 100000, city: "" })));
    }
  }, [assets]);

  const addRow = () => {
    if (rows.length >= 6) return;
    const used = new Set(rows.map((r) => r.asset_id));
    const next = assets.find((a) => !used.has(a.asset_id));
    if (next) setRows([...rows, { asset_id: next.asset_id, amount_usd: 100000, city: "" }]);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const run = async () => {
    setLoading(true);
    try {
      const d = await postComparator({ assets: rows, years, lang });
      setResult(d);
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    const hash = encodeState(years, rows);
    const url = `${window.location.origin}${window.location.pathname}#c=${hash}`;
    window.history.replaceState(null, "", `#c=${hash}`);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy link:", url);
    }
  };

  const exportPdf = async () => {
    if (!result) return;
    const user = getStoredUser();
    if (!user?.email) {
      window.dispatchEvent(new CustomEvent("goldora:open-register"));
      return;
    }
    const tableRows = [
      [lang === "pt" ? "Ativo" : "Asset", lang === "pt" ? "Valor inicial USD" : "Initial USD", lang === "pt" ? "Cidade" : "City"],
      ...rows.map((r) => [r.asset_id, String(r.amount_usd), r.city || "—"]),
    ];
    const seriesPayload = (result.series || []).map((s) => ({
      label: s.label,
      points: s.series.map((p) => ({ date: String(p.year), value: p.value })),
    }));
    try {
      const res = await fetch(`${API}/reports/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          title: lang === "pt" ? "Relatório de Comparação — GOLDORA" : "Comparison Report — GOLDORA",
          subtitle: `${lang === "pt" ? "Horizonte" : "Horizon"}: ${years} ${lang === "pt" ? "anos" : "years"}`,
          summary: result.overall_takeaway || "",
          rows: tableRows,
          series: seriesPayload,
          takeaway: result.overall_takeaway,
          lang,
        }),
      });
      if (res.status === 402) {
        const j = await res.json().catch(() => ({}));
        window.dispatchEvent(new CustomEvent("goldora:quota-blocked", { detail: { ...(j.detail || {}), feature: "pdf_export" } }));
        return;
      }
      if (!res.ok) throw new Error("pdf-failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `goldora-comparator-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("export pdf", e);
    }
  };

  const chartSeries = (result?.series || []).map((s, idx) => ({
    label: s.label,
    data: s.series.map((p) => ({ year: p.year, value: p.value })),
  }));

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-comparator">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          {lang === "pt" ? "Comparação cruzada" : "Cross comparison"}
        </span>
        <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.comparator.title}</h1>
        <p className="mt-3 max-w-2xl text-neutral-400">{t.comparator.sub}</p>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        {/* Controls */}
        <div className="lg:col-span-5 surface p-5 space-y-3" data-testid="comparator-controls">
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono flex-1">
              {t.comparator.label_years}
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={years}
              onChange={(e) => setYears(Math.max(1, Math.min(30, +e.target.value || 1)))}
              className="w-20 bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-right font-mono text-sm focus:outline-none focus:border-gold-500"
              data-testid="years-input"
            />
          </div>
          <div className="space-y-3 pt-2 border-t border-white/5">
            {rows.map((r, i) => {
              const meta = assets.find((a) => a.asset_id === r.asset_id);
              return (
                <div
                  key={i}
                  className="surface-elevated p-3 space-y-2"
                  data-testid={`row-asset-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <select
                      value={r.asset_id}
                      onChange={(e) => updateRow(i, { asset_id: e.target.value, city: "" })}
                      className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-gold-500"
                      data-testid={`asset-select-${i}`}
                    >
                      {assets.map((a) => (
                        <option key={a.asset_id} value={a.asset_id}>
                          {lang === "pt" ? a.name_pt : a.name_en}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeRow(i)}
                      className="text-neutral-500 hover:text-red-400 p-1.5 rounded"
                      data-testid={`remove-row-${i}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500 font-mono uppercase">USD</span>
                    <input
                      type="number"
                      value={r.amount_usd}
                      onChange={(e) => updateRow(i, { amount_usd: +e.target.value })}
                      className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-right font-mono text-sm focus:outline-none focus:border-gold-500"
                      data-testid={`amount-input-${i}`}
                    />
                  </div>
                  {r.asset_id === "real_estate" && (
                    <div data-testid={`location-${i}`}>
                      <LocationSelector
                        value={r.city}
                        onChange={(city) => updateRow(i, { city })}
                        testidPrefix={`comp-loc-${i}`}
                        optional
                        compact
                      />
                    </div>
                  )}
                  <div className="text-[11px] text-neutral-500 font-mono">
                    {lang === "pt" ? "Retorno esperado:" : "Expected return:"}{" "}
                    <span className="text-gold-400">
                      {meta ? `${meta.expected_cagr}% a.a.` : "—"}
                    </span>{" "}
                    · {lang === "pt" ? "Risco" : "Risk"}: {meta?.risk ?? "—"}/100
                  </div>
                </div>
              );
            })}
            <button
              onClick={addRow}
              disabled={rows.length >= 6}
              className="w-full flex items-center justify-center gap-2 text-sm bg-white/5 hover:bg-white/10 disabled:opacity-40 border border-dashed border-white/10 rounded-md py-2.5 transition-colors"
              data-testid="add-asset-btn"
            >
              <Plus size={14} />
              {t.common.add}
            </button>
          </div>
          <button
            onClick={run}
            disabled={loading || rows.length === 0}
            className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-40 text-black font-medium py-3 rounded-md transition-colors"
            data-testid="run-comparator"
          >
            {loading ? "…" : t.common.compare}
          </button>
          <button
            onClick={share}
            disabled={rows.length === 0}
            className="w-full mt-2 bg-white/5 hover:bg-white/10 border border-white/10 text-neutral-300 hover:text-white font-medium py-2.5 rounded-md transition-colors inline-flex items-center justify-center gap-2 text-sm"
            data-testid="share-comparator"
          >
            <Share2 size={14} />
            {copied ? t.common.copied : t.comparator.share_btn}
          </button>
          <button
            onClick={exportPdf}
            disabled={!result}
            className="w-full mt-2 bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/40 text-gold-300 font-medium py-2.5 rounded-md transition-colors inline-flex items-center justify-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="export-pdf-btn"
            title={lang === "pt" ? "Exportar PDF (Pro)" : "Export PDF (Pro)"}
          >
            <FileDown size={14} />
            {lang === "pt" ? "Exportar PDF (Pro)" : "Export PDF (Pro)"}
          </button>
        </div>

        {/* Result */}
        <div className="lg:col-span-7 space-y-6">
          <div className="surface p-5" data-testid="comparator-chart">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg">
                {lang === "pt" ? "Projeção em" : "Projection over"} {years} {t.common.years}
              </h3>
              <div className="flex flex-wrap gap-2">
                {(result?.series || []).map((s, i) => {
                  const colors = [COLORS.emerald, COLORS.gold, COLORS.sky, COLORS.violet, COLORS.pink, COLORS.red];
                  return (
                    <span
                      key={s.label}
                      className="flex items-center gap-1.5 text-xs font-mono text-neutral-300"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: colors[i % colors.length] }} />
                      {s.label}
                    </span>
                  );
                })}
              </div>
            </div>
            {result ? (
              <MultiLineChart series={chartSeries} years={years} height={320} testid="compare-chart" />
            ) : (
              <div className="h-[320px] grid place-items-center text-sm text-neutral-500">
                {lang === "pt" ? "Execute a comparação para ver o gráfico." : "Run the comparison to see the chart."}
              </div>
            )}
          </div>

          {result && (
            <div className="surface p-5" data-testid="comparator-summary">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg">{lang === "pt" ? "Resultado" : "Result"}</h3>
                <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                  {t.takeaway}
                </span>
              </div>
              <p className="text-sm text-neutral-300 mt-2">{result.overall_takeaway}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {result.summary.map((s) => (
                  <div key={s.asset_id + s.label} className="surface-elevated p-4" data-testid={`summary-${s.asset_id}`}>
                    <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono">{s.label}</div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="font-mono text-2xl text-white">
                        ${s.final_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                      <span className={`font-mono text-sm ${s.total_return_pct >= 0 ? "text-gold-400" : "text-red-400"}`}>
                        {s.total_return_pct >= 0 ? "+" : ""}
                        {s.total_return_pct.toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono">
                      <div>
                        <span className="text-neutral-500">{lang === "pt" ? "Risco" : "Risk"}: </span>
                        <span className="text-white">{s.risk_score}/100</span>
                      </div>
                      <div>
                        <span className="text-neutral-500">{lang === "pt" ? "Liquidez" : "Liquidity"}: </span>
                        <span className="text-white">{s.liquidity_score}/100</span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-neutral-400 leading-relaxed">{s.takeaway}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
